import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Checkbox,
  ListItemText,
  Chip,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material'
import {
  Analytics as AnalyticsIcon,
  AccountTree as IntegrationIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  TableChart as GoogleSheetsIcon,
  LocalShipping as TrackingIcon,
  Schedule as DeliveryIcon,
  CloudUpload as CloudUploadIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import { useProject } from '../contexts/ProjectContext'
import { useAuth } from '../contexts/AuthContext'
import GoogleSheetsIntegration from '../components/GoogleSheetsIntegration'
import GoogleSheetsOAuthDialog from '../components/GoogleSheetsOAuthDialog'
import dayjs from 'dayjs'

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`otdr-tabpanel-${index}`}
      aria-labelledby={`otdr-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const OTDRPage = () => {
  const { selectedProject, setSelectedProject } = useProject()
  const { user, isAuthenticated } = useAuth()
  const [activeTab, setActiveTab] = useState(0)
  const [otdrData, setOtdrData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [syncLoading, setSyncLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState({ tracking: false, delivery: false })
  
  // Google Sheets integration state
  const [showGoogleSheets, setShowGoogleSheets] = useState(false)
  const [projects, setProjects] = useState([])
  const [sheetsConfigs, setSheetsConfigs] = useState([])
  const [sheetsLoading, setSheetsLoading] = useState(false)
  
  // Date filters for analytics (only past dates allowed for OTDR analysis)
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
  
  // Multi-project and CSV import state
  const [isMultiProject, setIsMultiProject] = useState(false)
  const [selectedProjects, setSelectedProjects] = useState([])
  const [csvFile, setCsvFile] = useState(null)
  const [csvUploading, setCsvUploading] = useState(false)
  
  // Get today's date for max date restriction
  const today = dayjs().format('YYYY-MM-DD')

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const fetchOTDRAnalytics = async () => {
    if (!selectedProject && (!isMultiProject || selectedProjects.length === 0)) {
      setError('Please select a project to view OTDR analytics')
      return
    }
    
    setLoading(true)
    setError('')
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setError('Authentication token not found. Please login again.')
        setLoading(false)
        return
      }

      let url
      if (isMultiProject && selectedProjects.length > 0) {
        // Multi-project analytics
        const projectIdsParam = selectedProjects.map(id => `project_ids=${id}`).join('&')
        url = `/api/otdr/analytics/combined/multi-project?${projectIdsParam}&start_date=${startDate}&end_date=${endDate}`
      } else {
        // Single project analytics
        url = `/api/otdr/analytics/${selectedProject.id}?start_date=${startDate}&end_date=${endDate}`
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setOtdrData(data)
      } else if (response.status === 401) {
        setError('Authentication expired. Please login again.')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.detail || `Failed to fetch OTDR analytics (${response.status})`)
      }
    } catch (err) {
      setError('Network error: ' + err.message)
    }
    setLoading(false)
  }

  const syncExistingShipments = async () => {
    if (!selectedProject || !isAuthenticated) {
      setError('Please login to sync shipments')
      return
    }
    
    setSyncLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setError('Authentication token not found. Please login again.')
        setSyncLoading(false)
        return
      }

      const response = await fetch(
        `/api/otdr/sync-existing-shipments/${selectedProject.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const result = await response.json()
        alert(`✅ Sync Completed!\n📊 Processed: ${result.total_shipments_processed}\n🔄 Synced to OTDR: ${result.synced_to_otdr}\n🌐 API Refreshed: ${result.api_refreshed}`)
        fetchOTDRAnalytics() // Refresh analytics after sync
      } else if (response.status === 401) {
        setError('Authentication expired. Please login again.')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.detail || `Failed to sync shipments (${response.status})`)
      }
    } catch (err) {
      setError('Sync error: ' + err.message)
    }
    setSyncLoading(false)
  }

  // Poll task status
  const pollTaskStatus = async (taskId, type) => {
    const token = localStorage.getItem('access_token')
    if (!token) return null
    
    try {
      const response = await fetch(`/api/otdr/fill-missing-data/status/${taskId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        return await response.json()
      }
    } catch (err) {
      console.error('Status polling error:', err)
    }
    return null
  }

  // Manual tracking update - using background tasks
  const handleUpdateTracking = async () => {
    if (!selectedProject || !isAuthenticated) {
      setError('Please select a project and login to update tracking')
      return
    }
    
    setUpdateLoading(prev => ({ ...prev, tracking: true }))
    setError('')
    
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setError('Authentication token not found. Please login again.')
        setUpdateLoading(prev => ({ ...prev, tracking: false }))
        return
      }

      const response = await fetch(
        `/api/otdr/fill-missing-data/${selectedProject.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ type: 'tracking' })
        }
      )

      if (response.ok) {
        const result = await response.json()
        const taskId = result.task_id
        
        alert(`✅ Tracking Update Started!\n⏳ Processing in background...\nTask ID: ${taskId}`)
        
        // Poll for completion
        const pollInterval = setInterval(async () => {
          const status = await pollTaskStatus(taskId, 'tracking')
          
          if (status && status.status === 'completed') {
            clearInterval(pollInterval)
            setUpdateLoading(prev => ({ ...prev, tracking: false }))
            alert(`✅ Tracking Update Completed!\n🔄 Updated: ${status.updated_count}/${status.total_count} OTDR shipments`)
            fetchOTDRAnalytics() // Refresh analytics after update
          } else if (status && status.status === 'failed') {
            clearInterval(pollInterval)
            setUpdateLoading(prev => ({ ...prev, tracking: false }))
            setError(`Tracking update failed: ${status.error}`)
          }
        }, 5000) // Poll every 5 seconds
        
        // Stop polling after 30 minutes
        setTimeout(() => {
          clearInterval(pollInterval)
          setUpdateLoading(prev => ({ ...prev, tracking: false }))
        }, 30 * 60 * 1000)
        
      } else if (response.status === 401) {
        setError('Authentication expired. Please login again.')
        setUpdateLoading(prev => ({ ...prev, tracking: false }))
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.detail || 'Failed to start tracking update')
        setUpdateLoading(prev => ({ ...prev, tracking: false }))
      }
    } catch (err) {
      setError('Tracking update error: ' + err.message)
      setUpdateLoading(prev => ({ ...prev, tracking: false }))
    }
  }

  // Manual delivery update - using background tasks
  const handleUpdateDelivery = async () => {
    if (!selectedProject || !isAuthenticated) {
      setError('Please select a project and login to update delivery dates')
      return
    }
    
    setUpdateLoading(prev => ({ ...prev, delivery: true }))
    setError('')
    
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setError('Authentication token not found. Please login again.')
        setUpdateLoading(prev => ({ ...prev, delivery: false }))
        return
      }

      const response = await fetch(
        `/api/otdr/fill-missing-data/${selectedProject.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ type: 'delivery' })
        }
      )

      if (response.ok) {
        const result = await response.json()
        const taskId = result.task_id
        
        alert(`✅ Delivery Update Started!\n⏳ Processing in background...\nTask ID: ${taskId}`)
        
        // Poll for completion
        const pollInterval = setInterval(async () => {
          const status = await pollTaskStatus(taskId, 'delivery')
          
          if (status && status.status === 'completed') {
            clearInterval(pollInterval)
            setUpdateLoading(prev => ({ ...prev, delivery: false }))
            alert(`✅ Delivery Update Completed!\n📅 Updated: ${status.updated_count}/${status.total_count} OTDR shipments`)
            fetchOTDRAnalytics() // Refresh analytics after update
          } else if (status && status.status === 'failed') {
            clearInterval(pollInterval)
            setUpdateLoading(prev => ({ ...prev, delivery: false }))
            setError(`Delivery update failed: ${status.error}`)
          }
        }, 5000) // Poll every 5 seconds
        
        // Stop polling after 30 minutes
        setTimeout(() => {
          clearInterval(pollInterval)
          setUpdateLoading(prev => ({ ...prev, delivery: false }))
        }, 30 * 60 * 1000)
        
      } else if (response.status === 401) {
        setError('Authentication expired. Please login again.')
        setUpdateLoading(prev => ({ ...prev, delivery: false }))
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.detail || 'Failed to start delivery update')
        setUpdateLoading(prev => ({ ...prev, delivery: false }))
      }
    } catch (err) {
      setError('Delivery update error: ' + err.message)
      setUpdateLoading(prev => ({ ...prev, delivery: false }))
    }
  }

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) return

      const response = await fetch('/api/auth/projects', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setProjects(data)
      }
    } catch (error) {
      console.error('Failed to load projects:', error)
    }
  }

  const handleCsvExport = async () => {
    const targetProjectId = isMultiProject && selectedProjects.length > 0 
      ? selectedProjects[0] 
      : selectedProject?.id

    if (!targetProjectId && !isMultiProject) {
      setError('Please select a project')
      return
    }

    setCsvUploading(true)
    setError('')

    try {
      const token = localStorage.getItem('access_token')
      
      let url
      if (isMultiProject && selectedProjects.length > 0) {
        const projectIdsParam = selectedProjects.map(id => `project_ids=${id}`).join('&')
        url = `/api/otdr/export-csv/multi-project?${projectIdsParam}&start_date=${startDate}&end_date=${endDate}`
      } else {
        url = `/api/otdr/export-csv/${targetProjectId}?start_date=${startDate}&end_date=${endDate}`
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const blob = await response.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = downloadUrl
        
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('content-disposition')
        const filenameMatch = contentDisposition && contentDisposition.match(/filename="?(.+)"?/i)
        a.download = filenameMatch ? filenameMatch[1] : `otdr_export_${new Date().getTime()}.csv`
        
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(downloadUrl)
        document.body.removeChild(a)
        
        alert('✅ CSV exported successfully!')
      } else {
        const errorData = await response.json()
        setError(`CSV export failed: ${errorData.detail || 'Unknown error'}`)
      }
    } catch (err) {
      setError(`CSV export failed: ${err.message}`)
    } finally {
      setCsvUploading(false)
    }
  }

  const fetchSheetsConfigs = async () => {
    if (!selectedProject) return
    
    setSheetsLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(
        `/api/otdr/configs/project/${selectedProject.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const configs = await response.json()
        console.log('Fetched sheets configs:', configs)
        setSheetsConfigs(configs)
      } else {
        console.error('Failed to fetch sheets configs:', response.status, response.statusText)
      }
    } catch (err) {
      console.error('Failed to fetch sheets configs:', err)
    }
    setSheetsLoading(false)
  }

  const handleDisconnectSheets = async (configId) => {
    if (!window.confirm('Are you sure you want to disconnect this Google Sheets integration?')) {
      return
    }

    setSheetsLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(
        `${apiUrl}/otdr/configs/${configId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_active: false })
        }
      )

      if (response.ok) {
        alert('✅ Google Sheets integration disconnected successfully')
        fetchSheetsConfigs()
      } else {
        setError('Failed to disconnect Google Sheets')
      }
    } catch (err) {
      setError('Error disconnecting: ' + err.message)
    }
    setSheetsLoading(false)
  }

  const handleReconnectSheets = async (configId) => {
    setSheetsLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(
        `${apiUrl}/otdr/configs/${configId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_active: true })
        }
      )

      if (response.ok) {
        alert('✅ Google Sheets integration reconnected successfully')
        fetchSheetsConfigs()
      } else {
        setError('Failed to reconnect Google Sheets')
      }
    } catch (err) {
      setError('Error reconnecting: ' + err.message)
    }
    setSheetsLoading(false)
  }

  const handleSyncNow = async (configId) => {
    setSheetsLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(
        `${apiUrl}/otdr/configs/${configId}/sync`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const result = await response.json()
        alert(`✅ Sync completed! Created: ${result.awbs_created || 0}, Updated: ${result.awbs_updated || 0}`)
        fetchOTDRAnalytics()
      } else {
        setError('Sync failed')
      }
    } catch (err) {
      setError('Sync error: ' + err.message)
    }
    setSheetsLoading(false)
  }

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this Google Sheets integration? This action cannot be undone.')) {
      return
    }

    setSheetsLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(
        `${apiUrl}/otdr/configs/${configId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        alert('✅ Google Sheets integration deleted successfully')
        fetchSheetsConfigs()
      } else {
        setError('Failed to delete Google Sheets integration')
      }
    } catch (err) {
      setError('Error deleting: ' + err.message)
    }
    setSheetsLoading(false)
  }

  useEffect(() => {
    loadProjects()
  }, [])

  useEffect(() => {
    if ((selectedProject || (isMultiProject && selectedProjects.length > 0)) && activeTab === 0) {
      fetchOTDRAnalytics()
    }
    if (selectedProject && (activeTab === 0 || activeTab === 1)) {
      fetchSheetsConfigs()
    }
  }, [selectedProject, selectedProjects, isMultiProject, startDate, endDate, activeTab])

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Please login to access OTDR analytics and configuration.
        </Alert>
      </Box>
    )
  }

  if (!selectedProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please select a project to view OTDR analytics and configuration.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon color="primary" />
        OTDR Analytics & Integration
      </Typography>
      
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        On-Time Delivery Rate (OTDR) tracking for project: <strong>{selectedProject.name}</strong>
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="OTDR tabs"
          >
            <Tab 
              icon={<AnalyticsIcon />} 
              label="OTDR Dashboard" 
              id="otdr-tab-0"
              aria-controls="otdr-tabpanel-0"
            />
            <Tab 
              icon={<IntegrationIcon />} 
              label="Google Sheets Integration" 
              id="otdr-tab-1"
              aria-controls="otdr-tabpanel-1"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          {/* OTDR Analytics Dashboard */}
          <Box>
            <Typography variant="h5" gutterBottom>
              📊 OTDR Analytics Dashboard
            </Typography>
            
            {/* Multi-Project Selector */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Button 
                    variant={isMultiProject ? "contained" : "outlined"} 
                    onClick={() => {
                      setIsMultiProject(!isMultiProject)
                      if (!isMultiProject) setSelectedProjects([])
                    }}
                  >
                    {isMultiProject ? "Multi-Project Mode" : "Single Project"}
                  </Button>
                </Grid>
                
                {isMultiProject && (
                  <Grid item xs={12} sm={8}>
                    <FormControl fullWidth>
                      <InputLabel>Select Projects</InputLabel>
                      <Select
                        multiple
                        value={selectedProjects}
                        onChange={(e) => setSelectedProjects(e.target.value)}
                        label="Select Projects"
                        renderValue={(selected) => 
                          selected.map(id => projects.find(p => p.id === id)?.name || id).join(', ')
                        }
                      >
                        {projects.map((project) => (
                          <MenuItem key={project.id} value={project.id}>
                            <Checkbox checked={selectedProjects.indexOf(project.id) > -1} />
                            <ListItemText primary={project.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Date Range Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Start Date (Latest Delivery Date Range)"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today }}
                  helperText="Select past dates for analysis"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="End Date (Latest Delivery Date Range)"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today, min: startDate }}
                  helperText="End date must be after start date"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button 
                  variant="contained" 
                  onClick={fetchOTDRAnalytics}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  fullWidth
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button 
                  variant="outlined" 
                  startIcon={csvUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  onClick={handleCsvExport}
                  fullWidth
                  disabled={csvUploading}
                >
                  Export CSV
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button 
                  variant="outlined" 
                  onClick={syncExistingShipments}
                  disabled={syncLoading || isMultiProject}
                  startIcon={syncLoading ? <CircularProgress size={20} /> : <SyncIcon />}
                  fullWidth
                >
                  Sync AWBs
                </Button>
              </Grid>
            </Grid>

            {/* Manual Update Buttons - Same as Main Dashboard */}
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                🔄 Manual Data Updates
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Use these buttons to manually update OTDR shipment data using the same logic as the main dashboard.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Button 
                    variant="contained" 
                    onClick={handleUpdateTracking}
                    disabled={updateLoading.tracking || !selectedProject}
                    startIcon={updateLoading.tracking ? <CircularProgress size={20} /> : <TrackingIcon />}
                    fullWidth
                    color="primary"
                  >
                    {updateLoading.tracking ? 'Updating Tracking...' : 'Update Tracking Data'}
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button 
                    variant="contained" 
                    onClick={handleUpdateDelivery}
                    disabled={updateLoading.delivery || !selectedProject}
                    startIcon={updateLoading.delivery ? <CircularProgress size={20} /> : <DeliveryIcon />}
                    fullWidth
                    color="secondary"
                  >
                    {updateLoading.delivery ? 'Updating Delivery...' : 'Update Delivery Dates'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* OTDR Metrics Cards */}
            {otdrData && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Shipments
                      </Typography>
                      <Typography variant="h3" color="primary">
                        {otdrData.total_shipments}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        On-Time Deliveries
                      </Typography>
                      <Typography variant="h3" color="success.main">
                        {otdrData.on_time_count || otdrData.on_time_shipments}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Late Deliveries
                      </Typography>
                      <Typography variant="h3" color="error.main">
                        {otdrData.late_count || otdrData.late_shipments}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                    <CardContent>
                      <Typography color="inherit" gutterBottom>
                        On-Time %
                      </Typography>
                      <Typography variant="h3" color="inherit">
                        {(otdrData.on_time_percentage || otdrData.otdr_percentage || 0).toFixed(1)}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {otdrData.average_days_difference !== undefined && (
                  <Grid item xs={12} md={12}>
                    <Card>
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>
                          Average Days Difference
                        </Typography>
                        <Typography variant="h4" color={otdrData.average_days_difference >= 0 ? "success.main" : "error.main"}>
                          {otdrData.average_days_difference >= 0 ? '+' : ''}{otdrData.average_days_difference.toFixed(1)} days
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {(otdrData.average_days_early || otdrData.average_days_late) && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Average Days Early
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {otdrData.average_days_early ? `+${otdrData.average_days_early.toFixed(1)} days` : 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Average Days Late
                          </Typography>
                          <Typography variant="h4" color="error.main">
                            {otdrData.average_days_late ? `-${otdrData.average_days_late.toFixed(1)} days` : 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}

                {/* Multi-Project Breakdown */}
                {isMultiProject && otdrData.project_breakdowns && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          📊 Project Breakdown
                        </Typography>
                        <Grid container spacing={2}>
                          {otdrData.project_breakdowns.map((project) => (
                            <Grid item xs={12} md={6} key={project.project_id}>
                              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                  {project.project_name}
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Total:</Typography>
                                    <Typography variant="h6">{project.total_shipments}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">On-Time %:</Typography>
                                    <Typography variant="h6" color="primary">{project.on_time_percentage?.toFixed(1)}%</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">On-Time:</Typography>
                                    <Typography variant="body1" color="success.main">{project.on_time_count}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Late:</Typography>
                                    <Typography variant="body1" color="error.main">{project.late_count}</Typography>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Google Sheets Integration */}
          <Box>
            <Typography variant="h5" gutterBottom>
              📊 Google Sheets Integration
            </Typography>
            
            <Typography variant="body1" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
              Connect your Google Sheets to automatically import OTDR tracking data
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" gutterBottom>
                      Setup Google Sheets Integration
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Authenticate with Google, select your spreadsheet, and map columns to automatically sync OTDR data.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      startIcon={<GoogleSheetsIcon />}
                      onClick={() => {
                        loadProjects()
                        setShowGoogleSheets(true)
                      }}
                      fullWidth
                    >
                      Configure Google Sheets
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Active Google Sheets Integrations */}
            {selectedProject && sheetsConfigs.length > 0 && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GoogleSheetsIcon color="primary" />
                    Active Integrations
                  </Typography>
                  <Chip 
                    label={`${sheetsConfigs.filter(c => c.is_active).length} Active`} 
                    color={sheetsConfigs.some(c => c.is_active) ? "success" : "default"}
                    size="small"
                  />
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  {sheetsConfigs.map((config) => (
                    <Grid item xs={12} md={6} key={config.id}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {config.name}
                            </Typography>
                            <Box>
                              {config.is_active ? (
                                <Chip 
                                  icon={<CheckCircleIcon />} 
                                  label="Active" 
                                  color="success" 
                                  size="small" 
                                />
                              ) : (
                                <Chip 
                                  icon={<ErrorIcon />} 
                                  label="Inactive" 
                                  color="default" 
                                  size="small" 
                                />
                              )}
                            </Box>
                          </Box>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Sheet: {config.sheet_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                            📅 Auto-sync: Every 30 minutes
                          </Typography>
                          {config.last_synced_at && (
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                              🕐 Last synced: {new Date(config.last_synced_at).toLocaleString()}
                            </Typography>
                          )}
                          <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                            {config.is_active ? (
                              <>
                                <Tooltip title="Manually sync data from Google Sheets">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<SyncIcon />}
                                    onClick={() => handleSyncNow(config.id)}
                                    disabled={sheetsLoading}
                                  >
                                    Sync Now
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Disconnect this integration">
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<LinkOffIcon />}
                                    onClick={() => handleDisconnectSheets(config.id)}
                                    disabled={sheetsLoading}
                                  >
                                    Disconnect
                                  </Button>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Reconnect this integration">
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<LinkIcon />}
                                    onClick={() => handleReconnectSheets(config.id)}
                                    disabled={sheetsLoading}
                                  >
                                    Reconnect
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Delete this integration permanently">
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteConfig(config.id)}
                                    disabled={sheetsLoading}
                                  >
                                    Delete
                                  </Button>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {sheetsLoading && <LinearProgress sx={{ mt: 2 }} />}
              </Paper>
            )}

            {selectedProject && sheetsConfigs.length === 0 && (
              <Alert severity="info" sx={{ mb: 3 }}>
                No Google Sheets integrations configured yet. Click "Configure Google Sheets" above to get started.
              </Alert>
            )}

            <Alert severity="info">
              <Typography variant="body2">
                <strong>How it works:</strong>
                <br />
                1. Click "Configure Google Sheets" to authenticate with Google
                <br />
                2. Select your spreadsheet and worksheet tab
                <br />
                3. Map your columns (Order ID, Shipment ID, Latest Delivery Date, Actual Delivered Date)
                <br />
                4. Data will be automatically synchronized every 30 minutes for OTDR analysis
              </Typography>
            </Alert>
          </Box>
        </TabPanel>
      </Paper>

      {/* Google Sheets OAuth Dialog */}
      <GoogleSheetsOAuthDialog 
        open={showGoogleSheets} 
        onClose={() => setShowGoogleSheets(false)} 
        projects={projects}
        onSyncComplete={() => {
          fetchOTDRAnalytics()
          setShowGoogleSheets(false)
        }}
      />
    </Box>
  )
}

export default OTDRPage